import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useGetTemplateByPrincipalIdQuery,
  useUpdateTemplateMutation,
} from "../store/services/templateApi";
import { useFetchPrincipalEntitiesdataQuery } from "../store/services/principleApi";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CommonModal from "../component/common/commonModal";
import Grid from "@mui/material/Grid2";
import { toast } from "react-toastify";
import { skipToken } from "@reduxjs/toolkit/query";
import { cellSX, layoutCSS } from "../component/common/commonStyle";

import {
  useCreateHeaderMutation,
  useFetchHeaderQuery,
  useUpdateHeaderMutation,
  useGetHeaderQuery
} from "../store/services/headerApi ";
import { maskNumber } from "../component/common/Encryption";

const Template = () => {
  const [updateHeader] = useUpdateHeaderMutation();
  const [createTemplate] = useCreateTemplateMutation();
  const [deleteTemplate] = useDeleteTemplateMutation();
  const [updateTemplates] = useUpdateTemplateMutation();
  const [createHeader] = useCreateHeaderMutation();
  const [headerError, setHeaderError] = useState("");
  const [templatemodal, setTemplatemodal] = useState(false);
  const [editmodal, setEditmodal] = useState(false);
  const [headermodal, setHeadermodal] = useState(false);
  const [deletemodal, setDeletemodal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [eId, setEid] = useState("");
  const [newPrincipalEntityId, setNewPrincipalEntityId] = useState(null);
  const { data: getTemplate, refetch: templateFetch } =
    useGetTemplateByPrincipalIdQuery(eId ?? skipToken);
  const { data: getPrincipal } = useFetchPrincipalEntitiesdataQuery();
  const [tid, setTId] = useState("");
  const [message, setMessage] = useState("");
  const [templateData, setTemplateData] = useState([]);
  const [principalEntities, setPrincipalEntities] = useState([]);
  const [formVisible, setFormVisible] = useState(false);
  const [templateText, setTemplateText] = useState("");
  const [templateType, setTemplateType] = useState("");
  const [traiTemplateId, setTraiTemplateId] = useState("");
  const [tempdata, setTempData] = useState(null);
  const [selectedHeaderId, setSelectedHeaderId] = useState("");
  const { data: headersByStatus } = useGetHeaderQuery(templateType || skipToken);
  const { data: getHeader, refetch: headerFetch } = useFetchHeaderQuery(
    eId && tid ? { id: eId, template_id: tid } : skipToken,
  );
  const [popupVisible, setPopupVisible] = useState(false);
  const [fetch, setFetch] = useState(false);
  const [enable, setEnable] = useState(false);
  const [header_name, setHeaderName] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (newPrincipalEntityId && templateFetch) {
      templateFetch();
    }
    setFormVisible(false);
  }, [newPrincipalEntityId, fetch, deletemodal, popupVisible, eId]);

  useEffect(() => {
    if (tid) {
      headerFetch();
    }
  }, [tid, headerFetch]);

  const handleSaveEdit = async () => {
    try {
      const response = await updateTemplates({
        id: tempdata.template_id,
        template_name: templateName,
        template_text: templateText,
        template_type: templateType,
        ...(selectedHeaderId && { header_id: selectedHeaderId }),
        trai_template_id: traiTemplateId,
      }).unwrap();

      toast.success(response?.message || "Template updated successfully");

      setPopupVisible(false);
      setEditmodal(false);
    } catch (error) {
      console.error("Update template error:", error);
      const errorMessage =
        error?.data?.message ||
        (error?.data?.errors && Object.values(error.data.errors)[0]) ||
        "Failed to update template";
      toast.error(errorMessage);
    }
  };

  const handlePrincipalEntityChange = async (e) => {
    const selectedId = e.target.value;
    setNewPrincipalEntityId(selectedId);
    setEid(selectedId);

    if (selectedId && templateFetch) {
      templateFetch();
    } else {
      setTemplateData([]);
      setMessage("No templates available for this principal entity");
    }
  };

  const handleSubmit = async () => {
    setFormErrors({});

    if (/\s/.test(templateName)) {
      toast.error("Template name must not contain spaces");
      return;
    }
    if (templateName.length > 30) {
      toast.error("Template name must be maximum 30 characters");
      return;
    }
    if (!templateText || !templateType || !templateName || !traiTemplateId) {
      toast.error("All fields are required");
      return;
    }

    const data = {
      trai_template_id: traiTemplateId,
      template_name: templateName,
      template_text: templateText,
      template_type: templateType,
      principalentity_id: newPrincipalEntityId,
      ...(selectedHeaderId && { header_id: selectedHeaderId }),
    };

    try {
      const response = await createTemplate(data).unwrap();
      toast.success(response?.message || "Template created successfully");
      resetForm();
      setTemplatemodal(false);
      templateFetch();
    } catch (error) {
      console.error("Error creating template:", error);
      const errorMessage =
        error?.data?.message ||
        (error?.data?.errors && Object.values(error.data.errors)[0]) ||
        "Failed to create template";
      toast.error(errorMessage);
    }
  };

  const handleDeleteTemplate = async () => {
    const res = await deleteTemplate(tid);
    if (res?.data) {
      toast.success("Deleted");
      setDeletemodal(false);
    } else {
      toast.error("Failed to delete");
      setDeletemodal(false);
    }
  };

  const handleUpdateHeader = async (header_id, isSelected) => {
    try {
      await updateHeader({
        template_id: tid,
        header_id: header_id,
        action: isSelected ? "unselect" : "select",
      }).unwrap();

      toast.success(
        `Header ${isSelected ? "unselected" : "selected"} successfully`,
      );
      headerFetch();
    } catch (error) {
      console.error("Error updating header:", error);
      toast.error(error?.data?.message || "Failed to update header selection");
    }
  };

  const handleAddHeader = async () => {
    if (!newPrincipalEntityId) {
      toast.error("Please select a Principal Entity first.");
      return;
    }

    const selectedPrincipalEntity = getPrincipal?.data?.find(
      (item) => item.principalentity_id === newPrincipalEntityId,
    );

    if (!selectedPrincipalEntity) {
      toast.error("Invalid Principal Entity selected.");
      return;
    }

    try {
      const res = await createHeader({
        header_name,
        principalentity_id: selectedPrincipalEntity.principalentity_id,
      });

      if (res?.error) {
        setHeaderError(res.error.data.message);
      } else {
        setEnable(false);
        setHeaderError("");
        toast.success("Header created successfully");
        headerFetch();
      }
    } catch (error) {
      setHeaderError("An unexpected error occurred");
      console.error(error);
    }
  };

  const resetForm = () => {
    setTemplateName("");
    setTemplateText("");
    setTemplateType("");
    setTraiTemplateId("");
  };

  return (
    <Box sx={layoutCSS}>
      <Typography
        sx={{ mb: 2, fontSize: "0.9rem", fontWeight: "bold" }}
        variant="h6"
      >
        Template List
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: 40,
          mb: 4,
        }}
      >
        <FormControl size="small" sx={{ width: 150 }}>
          <InputLabel
            id="demo-simple-select-label"
            sx={{ fontSize: "0.75rem" }}
          >
            Select EntityID
          </InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={newPrincipalEntityId}
            label="Select Principal Entity"
            onChange={handlePrincipalEntityChange}
            sx={{
              fontSize: "0.75rem",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "6px 8px",
              },
            }}
          >
            {getPrincipal?.data?.map((item, index) => (
              <MenuItem
                value={item.principalentity_id}
                key={index}
                sx={{ fontSize: "0.75rem" }}
              >
                {maskNumber(item?.pe_id)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          sx={{ padding: "6px", fontSize: "0.75rem" }}
          variant="contained"
          onClick={() => {
            resetForm();
            setTemplatemodal(true);
          }}
          disabled={!newPrincipalEntityId}
        >
          {formVisible ? "Close Form" : "Add New Template"}
        </Button>
      </Box>
      {message && (
        <div className="mt-4 text-center text-green-600">{message}</div>
      )}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 440, borderRadius: 0.5 }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead
              sx={{
                bgcolor: "#0288d1",
                "& th": {
                  color: "white",
                  fontSize: "0.75rem",
                  bgcolor: "#0288d1",
                  borderBottom: "0.5px solid #1b1b1bff",
                  padding: "5px",
                  borderRight: "0.5px solid #1b1b1bff",
                },
              }}
            >
              <TableRow>
                <TableCell>S.N</TableCell>
                <TableCell>TemplateID</TableCell>
                <TableCell>Template Name</TableCell>
                <TableCell>Template Text</TableCell>
                <TableCell>Template Type</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getTemplate?.data?.map((item, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& td": {
                      borderRight: "0.5px solid #1b1b1bff",
                      borderBottom: "0.5px solid #1b1b1bff",
                      padding: "8px",
                    },
                  }}
                >
                  <TableCell sx={cellSX}>{item?.template_id}</TableCell>
                  <TableCell sx={cellSX}>
                    {maskNumber(item?.trai_template_id)}
                  </TableCell>
                  <TableCell sx={cellSX}>{item?.template_name}</TableCell>
                  <TableCell sx={cellSX}>{item?.template_text}</TableCell>
                  <TableCell sx={cellSX}>{item?.template_type}</TableCell>
                  <TableCell sx={cellSX}>
                    <DeleteIcon
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        setDeletemodal(true);
                        setTId(item.template_id);
                      }}
                    />
                    <EditIcon
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        setTempData(item);
                        setTemplateText(item.template_text);
                        setTemplateType(item.template_type);
                        setSelectedHeaderId(item.header_id || "");
                        setTemplateName(item.template_name);
                        setTraiTemplateId(item.trai_template_id);
                        setEditmodal(true);
                      }}
                    />
                    <VisibilityIcon
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        setHeadermodal(true);
                        setTId(item.template_id);
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Typography sx={{ fontSize: "0.7rem", textAlign: "right" }}>
        {templateName.length}/30
      </Typography>

      {/* Create template modal */}
      <CommonModal
        open={templatemodal}
        setOpen={setTemplatemodal}
        width={400}
        PaperProps={{
          sx: {
            background: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(8px)",
          },
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontSize: "0.75rem" }}>
          Create Template
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Template Name"
            value={templateName}
            onChange={(e) => {
              const value = e.target.value;
              if (!/\s/.test(value)) {
                setTemplateName(value);
              }
            }}
            inputProps={{ maxLength: 30 }}
            sx={{
              fontSize: "0.75rem",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "10px 10px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.75rem",
              },
            }}
          />
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Template Text"
            value={templateText}
            onChange={(e) => setTemplateText(e.target.value)}
            multiline
            rows={4}
            sx={{
              fontSize: "0.75rem",
              "& textarea": {
                padding: "1px 1px",
                fontSize: "0.75rem",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.75rem",
              },
            }}
          />
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="template-type-label" sx={{ fontSize: "0.75rem" }}>
            Template Type
          </InputLabel>
          <Select
            labelId="template-type-label"
            id="template-type-select"
            value={templateType}
            label="Template Type"
            onChange={(e) => setTemplateType(e.target.value)}
            sx={{
              fontSize: "0.75rem",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "10px 10px",
              },
            }}
          >
            <MenuItem value="implicit" sx={{ fontSize: "0.75rem" }}>
              Implicit
            </MenuItem>
            <MenuItem value="explicit" sx={{ fontSize: "0.75rem" }}>
              Explicit
            </MenuItem>
            <MenuItem value="promotional" sx={{ fontSize: "0.75rem" }}>
              Promotional
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="header-select-label" sx={{ fontSize: "0.75rem" }}>
            Header
          </InputLabel>
          <Select
            labelId="header-select-label"
            id="header-select"
            value={selectedHeaderId}
            label="Header"
            onChange={(e) => setSelectedHeaderId(e.target.value)}
            disabled={!templateType || !headersByStatus?.data?.length}
            sx={{
              fontSize: "0.75rem",
              "& .MuiInputBase-input": { fontSize: "0.75rem", padding: "10px 10px" },
            }}
          >
            <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
              None
            </MenuItem>
            {headersByStatus?.data?.map((h) => (
              <MenuItem key={h.header_id} value={h.header_id} sx={{ fontSize: "0.75rem" }}>
                {h.header_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="DLT Template Id"
            value={traiTemplateId}
            onChange={(e) => setTraiTemplateId(e.target.value)}
            sx={{
              fontSize: "0.75rem",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "10px 10px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.75rem",
              },
            }}
          />
        </FormControl>

        <Box>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            size="small"
            sx={{ fontSize: "0.7rem" }}
          >
            Add Template
          </Button>
        </Box>
      </CommonModal>

      {/* Edit Template Modal */}
      <CommonModal
        open={editmodal}
        setOpen={setEditmodal}
        width={400}
        PaperProps={{
          sx: {
            background: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(8px)",
          },
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontSize: "0.75rem" }}>
          Edit Template
        </Typography>
        <Typography variant="h6" sx={{ mb: 2, fontSize: "0.75rem" }}>
          Template Name: {templateName}
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Template Text"
            value={templateText}
            onChange={(e) => setTemplateText(e.target.value)}
            multiline
            rows={4}
            sx={{
              fontSize: "0.75rem",
              "& textarea": {
                padding: "1px 1px",
                fontSize: "0.75rem",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.75rem",
              },
            }}
          />
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel
            id="edit-template-type-label"
            sx={{ fontSize: "0.75rem" }}
          >
            Template Type
          </InputLabel>
          <Select
            labelId="edit-template-type-label"
            id="edit-template-type-select"
            value={templateType}
            label="Template Type"
            onChange={(e) => setTemplateType(e.target.value)}
            sx={{
              fontSize: "0.75rem",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "10px 10px",
              },
            }}
          >
            <MenuItem value="implicit" sx={{ fontSize: "0.75rem" }}>
              Implicit
            </MenuItem>
            <MenuItem value="explicit" sx={{ fontSize: "0.75rem" }}>
              Explicit
            </MenuItem>
            <MenuItem value="promotional" sx={{ fontSize: "0.75rem" }}>
              Promotional
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="edit-header-select-label" sx={{ fontSize: "0.75rem" }}>
            Header
          </InputLabel>
          <Select
            labelId="edit-header-select-label"
            id="edit-header-select"
            value={selectedHeaderId}
            label="Header"
            onChange={(e) => setSelectedHeaderId(e.target.value)}
            disabled={!templateType || !headersByStatus?.data?.length}
            sx={{
              fontSize: "0.75rem",
              "& .MuiInputBase-input": { fontSize: "0.75rem", padding: "10px 10px" },
            }}
          >
            <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
              None
            </MenuItem>
            {headersByStatus?.data?.map((h) => (
              <MenuItem key={h.header_id} value={h.header_id} sx={{ fontSize: "0.75rem" }}>
                {h.header_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="DLT Template Id"
            value={traiTemplateId}
            onChange={(e) => setTraiTemplateId(e.target.value)}
            sx={{
              fontSize: "0.75rem",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "10px 10px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.75rem",
              },
            }}
          />
        </FormControl>

        <Box>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSaveEdit}
            size="small"
            sx={{ fontSize: "0.7rem" }}
          >
            Update Template
          </Button>
        </Box>
      </CommonModal>

      {/* view header modal */}
      <CommonModal
        open={headermodal}
        setOpen={setHeadermodal}
        width={!enable ? 600 : 400}
        PaperProps={{
          sx: {
            background: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(8px)",
          },
        }}
      >
        {!enable ? (
          <>
            <Typography
              variant="h6"
              sx={{
                mb: 1,
                fontSize: "0.8rem",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              Template Headers List
            </Typography>
            <Table sx={{ border: "1px solid #1b1b1bff" }}>
              <TableHead
                sx={{
                  bgcolor: "#0288d1",
                  "& th": {
                    color: "white",
                    fontSize: "0.75rem",
                    padding: "6px",
                    borderBottom: "0.5px solid #1b1b1bff",
                    borderRight: "0.5px solid #1b1b1bff",
                  },
                }}
              >
                <TableRow>
                  <TableCell>Select</TableCell>
                  <TableCell>Header Id</TableCell>
                  <TableCell>Header Name</TableCell>
                  <TableCell>isactive</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {getHeader?.headers?.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "& td": {
                        borderRight: "0.5px solid #1b1b1bff",
                        borderBottom: "0.5px solid #1b1b1bff",
                        padding: "2px",
                      },
                    }}
                  >
                    <TableCell sx={cellSX}>
                      <Checkbox
                        sx={{
                          color: "#dfdfdfff",
                          "& .MuiSvgIcon-root": {
                            color: "#eeeeeeff",
                          },
                        }}
                        onChange={() =>
                          handleUpdateHeader(item.header_id, item.isSelected)
                        }
                        checked={item?.isSelected}
                      />
                    </TableCell>
                    <TableCell sx={cellSX}>{item?.header_id}</TableCell>
                    <TableCell sx={cellSX}>{item?.header_name}</TableCell>
                    <TableCell sx={cellSX}>
                      {item?.is_active ? (
                        <Chip color="primary" label="Active" />
                      ) : (
                        <Chip label="In-Active" color="error" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Button
              variant="outlined"
              onClick={() => setEnable(!enable)}
              size="small"
              sx={{
                fontSize: "0.75rem",
                textTransform: "none",
                padding: "4px 12px",
                borderRadius: "6px",
                marginTop: 2,
                background: "#7c3aed",
                color: "white"
              }}
            >
              Add Header
            </Button>
          </>
        ) : (
          <>
            <TextField
              fullWidth
              label="Header Name"
              value={header_name}
              onChange={(e) => setHeaderName(e.target.value)}
              error={Boolean(headerError)}
              helperText={headerError}
              sx={{
                fontSize: "0.75rem",
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                  padding: "10px 10px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 2,
                gap: 2,
              }}
            >
              <Button
                onClick={() => handleAddHeader()}
                variant="contained"
                size="small"
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "none",
                  padding: "4px 12px",
                  borderRadius: "6px",
                }}
                disabled={!newPrincipalEntityId || !header_name.trim()}
              >
                Add Header
              </Button>

              <Button
                onClick={() => setEnable(!enable)}
                variant="outlined"
                color="error"
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "none",
                  padding: "4px 12px",
                  borderRadius: "6px",
                }}
              >
                Reset
              </Button>
            </Box>
          </>
        )}
      </CommonModal>

      {/* delete Modal */}
      <CommonModal
        open={deletemodal}
        setOpen={setDeletemodal}
        width={400}
        PaperProps={{
          sx: {
            background: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(8px)",
          },
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontSize: "0.9rem" }}>
          Are you sure you want to delete this template?
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            onClick={() => handleDeleteTemplate()}
            color="error"
            variant="contained"
            size="small"
            sx={{ fontSize: "0.75rem", width: "30%" }}
          >
            Yes
          </Button>
          <Button
            onClick={() => setDeletemodal(false)}
            size="small"
            sx={{ fontSize: "0.75rem", width: "30%" }}
            variant="outlined"
            color="error"
          >
            Cancel
          </Button>
        </Box>
      </CommonModal>
    </Box>
  );
};

export default Template;